import { createBehavior } from '@area17/a17-behaviors';
import createObserver from '../utils/createObserver';
import { cssTimeToMilliseconds } from '../utils/cssTimeToMilliseconds';

function wrap(el, wrapper) {
  el.parentNode.insertBefore(wrapper, el);
  wrapper.appendChild(el);
}

const textreveal = createBehavior(
  'textreveal',
  {
    splitText() {
      // const words = [];

      this.measureEl = null;

      for (const node of this.$el.childNodes) {
        if (node.nodeType === 3) {
          const text = node.textContent.trim();
          if (text === '') {
            continue;
          }

          // text
          const newNode = document.createElement('span');
          // newNode.classList.add('flex', 'flex-wrap');
          const words = node.wholeText.trim().split(' ');
          let i = 0;

          for (const word of words) {
            // Word (groups both inner and measure together)
            const wordSpan = document.createElement('span');
            wordSpan.setAttribute('data-textreveal-word', '');

            // Used for transform mask
            const innerSpan = document.createElement('span');
            innerSpan.setAttribute('data-textreveal-inner', '');
            // innerSpan.classList.add('inline-flex');

            // Used for transform translation
            const leafSpan = document.createElement('span');
            leafSpan.innerHTML = word;
            leafSpan.classList.add('inline-flex'); // to enable transform
            innerSpan.appendChild(leafSpan);

            // Append inner
            wordSpan.append(innerSpan);

            // Append measure (if it's not the last word)
            if (i < words.length - 1 || words.length === 1) {
              const measureSpan = document.createElement('span');
              // Use whitespace for measurement,
              // (as well as reintroducing the space from the split, if necessary)
              measureSpan.innerHTML = words.length === 1 ? '' : ' ';
              this.measureEl = measureSpan;
              wordSpan.append(measureSpan);
            }

            newNode.append(wordSpan);

            i++;
          }
          this.$el.replaceChild(newNode, node);
        } else if (node.nodeType === 1) {
          const innerSpan = document.createElement('span');
          innerSpan.setAttribute('data-textreveal-inner', '');

          // const leafSpan = document.createElement('span');
          // leafSpan.classList.add('inline-flex'); // to enable transform

          // wrap(node, leafSpan);
          wrap(node, innerSpan);

          node.classList.add('inline-flex');
        }
      }
    },
    measure() {
      const wordNodes = this.$el.querySelectorAll('[data-textreveal-word]');
      let currentTop = null;
      let line = 0;
      for (const node of wordNodes) {
        const top = node.offsetTop;
        if (currentTop == null || top > currentTop) {
          currentTop = top;
          line++;
        }
        node.style.setProperty('--textreveal-line', line);
      }
      this.$el.style.setProperty('--textreveal-line-total', line);
    },
    measureDimensions() {
      if (this.measureEl) {
        const lineBox = this.measureEl.getBoundingClientRect();
        this.$node.style.setProperty('--line-box', `${lineBox.height}px`);
      }

      this.$node.style.setProperty(
        '--line-height',
        window.getComputedStyle(this.$el).getPropertyValue('line-height')
      );
    },
    createIntersectionObserver($el, cb) {
      this.observer = createObserver($el, (entries) => {
        for (let entry of entries) {
          if (entry.intersectionRatio > 0) {
            cb();
          }
        }
      });
    },
    reveal() {
      // Set what the latest state *should* be
      this.shouldBeRevealed = true;

      // Exit early if an animation is running
      if (this.animationTimeout) {
        return;
      }

      // Set the actual state
      this.revealed = true;

      // Start the reveal CSS transition
      this.$node.classList.add('is-revealed');
      this.$node.classList.add('textreveal:is-revealed');

      this.animationTimeout = setTimeout(() => {
        this.animationTimeout = null;

        if (this.$outer && this.options.disableInitial) {
          this.$outer.classList.remove('pointer-events-none');
        }

        if (!this.shouldBeRevealed) {
          setTimeout(() => {
            this.unreveal();
          }, 50);
        }
      }, this.duration);
    },
    unreveal() {
      // Set what the latest state *should* be
      this.shouldBeRevealed = false;

      // Exit early if an animation is running
      if (this.animationTimeout) {
        return;
      }

      // Set the actual state
      this.revealed = false;

      // Start the unreveal CSS transition
      this.$node.classList.add('is-unrevealed');
      this.$node.classList.add('textreveal:is-unrevealed');
      if (this.$outer && this.options.disableInitial) {
        this.$outer.classList.add('pointer-events-none');
      }

      this.animationTimeout = setTimeout(() => {
        this.animationTimeout = null;

        // Cleanup the unreveal CSS transition (back to initial state)
        this.$node.classList.remove('is-revealed');
        this.$node.classList.remove('textreveal:is-revealed');
        this.$node.classList.remove('is-unrevealed');
        this.$node.classList.remove('textreveal:is-unrevealed');

        // Check reveal was attempted to be called,
        // if so, call it again now that the animationTimeout has finished.
        if (this.shouldBeRevealed) {
          setTimeout(() => {
            this.reveal();
          }, 50);
        }
      }, this.duration);
    },
    onWindowResize() {
      this.measureDimensions();
    },
    onReady() {
      if (!this.$node) {
        return;
      }

      const parentMenu = this.$node.closest('[data-behavior="menu"]');
      const parentInview = this.$node.closest('[data-behavior="inview"]');
      const parentNav = this.$node.closest('[data-behavior="nav"]');

      if (parentMenu) {
        parentMenu.addEventListener('menu:open', () => {
          this.reveal();
        });
        parentMenu.addEventListener('menu:close', () => {
          this.unreveal();
        });
      } else if (parentInview) {
        parentInview.addEventListener('inview:is-visible', () => {
          this.reveal();
        });
      } else if (parentNav) {
        this.scrolled = false;
        const $menu = document.querySelector('[data-behavior="menu"]');

        $menu.addEventListener('menu:open', () => {
          this.unreveal();
        });
        $menu.addEventListener('menu:close', () => {
          if (!this.scrolled) {
            this.reveal();
          }
        });

        this.createIntersectionObserver(this.$el, () => {
          setTimeout(() => {
            if (!this.revealed && !this.scrolled) {
              this.reveal();
            }
          }, 100);
        });

        parentNav.addEventListener('nav:scrolled', (evt) => {
          const { scrolled } = evt.detail;
          this.scrolled = scrolled;
          if (scrolled) {
            this.unreveal();
          } else {
            this.reveal();
          }
        });

        if (parentNav.classList.contains('nav:is-scrolled')) {
          this.scrolled = true;
        }
      } else {
        this.createIntersectionObserver(this.$el, () => {
          if (!this.revealed) {
            this.reveal();
          }
        });
      }
      window.addEventListener('resize', this.onWindowResize);
    },
  },
  {
    init() {
      this.options.disableInitial = this.options['disable-initial'] != null;
      this.$outer = this.$node.closest('[data-textreveal-outer]');
      this.$el = this.getChild('el') ?? this.$node;
      this.duration = cssTimeToMilliseconds(
        getComputedStyle(this.$node).getPropertyValue('--textreveal-duration')
      );
      this.delay = cssTimeToMilliseconds(
        getComputedStyle(this.$node).getPropertyValue('--textreveal-delay')
      );

      this.splitText();
      this.measure();
      this.measureDimensions();

      if (this.$outer) {
        if (this.options.disableInitial) {
          this.$outer.classList.add('pointer-events-none');
        }
      }

      // Wait for the content to load first, then fire...
      if (this.$node.closest('.prevent-transitions')) {
        document.addEventListener('content-is-loaded', () => {
          this.readyTimeout = setTimeout(() => {
            this.onReady();
          }, 0);
        });
      } else {
        this.onReady();
      }
    },
    destroy() {
      window.removeEventListener('resize', this.onWindowResize);
      this.observer.unobserve(this.$node);
      if (this.readyTimeout) {
        clearTimeout(this.readyTimeout);
      }
    },
  }
);

export default textreveal;
